import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { PermissionsService } from './permissions.service';
import { PermissionCheck } from './types/permissions.types';

@Injectable({
  providedIn: 'root',
})
export class PermissionGuard {
  constructor(
    private permissionsService: PermissionsService,
    private router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot): boolean | UrlTree {
    // Check if using new array format for multiple permission checks
    const permissionChecks = route.data[
      'permissionChecks'
    ] as PermissionCheck[];

    let isPermitted: boolean;

    if (permissionChecks && Array.isArray(permissionChecks)) {
      // New format: array of permission checks (ALL must pass)
      isPermitted = permissionChecks.every((check) =>
        this.permissionsService.isPermitted(
          check.permissions,
          check.key || 'View'
        )
      );
    } else {
      // Legacy format: single permission check
      const requiredPermissions = route.data['permissions'] as string[];
      isPermitted = this.permissionsService.isPermitted(
        requiredPermissions,
        route.data['key']
      );
    }

    if (isPermitted) {
      return true;
    }

    // If there's a custom redirect URL, use it
    if (route.data['redirectUrl']) {
      return this.router.parseUrl(route.data['redirectUrl']);
    }

    // If this is a child route and unauthorized, redirect to parent route
    // This allows the parent component to handle fallback to a permitted child route
    if (route.parent) {
      const parentPath = this.buildParentPath(route);
      if (parentPath) {
        return this.router.parseUrl(parentPath);
      }
    }

    // Default: redirect to no-permission page
    return this.router.parseUrl('/auth/no-permission');
  }

  private buildParentPath(route: ActivatedRouteSnapshot): string {
    const segments: string[] = [];
    let current: ActivatedRouteSnapshot | null = route.parent;

    while (current) {
      if (current.url.length > 0) {
        segments.unshift(...current.url.map((segment) => segment.path));
      }
      current = current.parent;
    }

    return '/' + segments.join('/');
  }
}
